import { EditorContent, type EditorEvents } from '@tiptap/react';
import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { asRect } from '../../../../../common/maths/geometry/rect';
import { TABLE_CELL_EDITOR_KEY } from '../../../../../common/table/tableConstants';
import { type TextAlignment } from '../../../../../common/table/CellTypeConstants';
import type { CellData, TiptapCellEditingSession } from '../../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { MilanoteEditorRef } from '../../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import useClientElementTiptapEditor from '../../../../components/tiptapEditor/useClientElementTiptapEditor';
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';
import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { getTableCellEditorId } from '../../bridge/utils/tableEditorSessionUtils';
import { getTiptapContentFromPlainText } from './tiptapCellEditorContentUtils';
import { initialiseTiptapCellEditorContent } from './tiptapCellEditorInitialisationUtils';
import { useTableCellEditorTiptapExtensions } from './useTableCellTiptapExtensions';
import { useTiptapCellEditorShortcutContext } from './useTiptapCellEditorShortcutContext';
import { getPlatformDetailsSelector } from '../../../../platform/platformSelector';
import { getEditorFocusIndex } from '../../../../../common/tiptap/utils/tiptapEditorFocusPositionUtils';
import type TiptapCellEditor from './TiptapCellEditor';
import type { Rect } from '../../../../../common/maths/geometry/rect/rectTypes';

type Props = {
    elementId: string;
    isEditable: boolean;
    cellData: CellData | undefined;
    cellEditingSession: TiptapCellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef<TiptapContent> | null>;
    textAlignment: TextAlignment | null;
    recomputeEditingTextAlignment: (editCellValue: string) => void;
    startEditingCellEditor: (editorFocusRect?: Rect, preventInitialFocus?: boolean) => boolean;
};

const TiptapCellEditorComponent: React.FC<Props> = ({
    elementId,
    isEditable,
    cellData,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    cellEditorRef,
    textAlignment,
    recomputeEditingTextAlignment,
    startEditingCellEditor,
}) => {
    const platformDetails = useSelector(getPlatformDetailsSelector);

    const tiptapEditorRef = useRef<HTMLDivElement | null>(null);
    const cellEditingSessionRef = useUpdatedRef(cellEditingSession);
    const recomputeEditingTextAlignmentRef = useUpdatedRef(recomputeEditingTextAlignment);

    const saveContent = useCallback(
        (content: TiptapContent) => {
            if (!cellEditingSessionRef.current) return;

            recomputeEditingTextAlignmentRef.current(getTiptapNodeTextContent(content));

            const activeEditor = hotTableInstanceRef.current?.getMNEditor();
            activeEditor?.onMilanoteCellEditorChange(content);

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', content);
        },
        [hotTableInstanceRef],
    );

    const extensions = useTableCellEditorTiptapExtensions({
        isEditable,
        saveContent,
        hotTableInstanceRef,
        cellEditingSessionRef,
    });

    const { editor } = useClientElementTiptapEditor({
        persistedContent: cellEditingSession?.persistedContent || null,
        // Cell content is owned by this edit session, so a deferred sync can overwrite the initial typed character.
        shouldSyncPersistedContent: false,
        extensions,

        editorId: getTableCellEditorId(elementId),
        editorKey: TABLE_CELL_EDITOR_KEY,
        currentEditorId: currentlyEditingEditorId || undefined,

        isEditable,
        isEditing: !!cellEditingSession,
        preventInitialFocus: !!cellEditingSession?.preventInitialFocus,
        isSingleSelected: true,
    });

    const activateEditorShortcutContext = useTiptapCellEditorShortcutContext({
        cellEditingSession,
        editor,
        hotTableInstanceRef,
    });

    useLayoutEffect(() => {
        if (!cellEditingSession || !editor) return;

        const { event, persistedContent, overwriteContent, shouldFocusAtClickPosition } = cellEditingSession;

        initialiseTiptapCellEditorContent({ editor, persistedContent, overwriteContent });

        // Only use the click position when the rendered cell content matches the editor content.
        const caretIndex = getEditorFocusIndex(editor, shouldFocusAtClickPosition ? event : undefined, platformDetails);

        editor.commands.setTextSelection(caretIndex);

        const activeEditor = hotTableInstanceRef.current?.getMNEditor() as TiptapCellEditor | undefined;
        if (!activeEditor) return;

        const editorFocusRect = asRect(editor.view.coordsAtPos(caretIndex));
        const hasStartEditingCellEditor = startEditingCellEditor(
            editorFocusRect,
            cellEditingSession.preventInitialFocus,
        );
        if (!hasStartEditingCellEditor) return;

        activeEditor.beginHandsontableEditing(overwriteContent, event);
    }, [cellEditingSession, editor, hotTableInstanceRef, platformDetails]);

    useLayoutEffect(() => {
        if (!cellEditingSession || !editor) return;

        // Keep plugin hooks in sync with the initial editor content when edit mode opens.
        // (eg formula reference highlighting would otherwise wait for the first edit rather than showing immediately)
        // @ts-ignore - Custom Milanote hook
        hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', editor.getJSON());
    }, [cellEditingSession, editor, hotTableInstanceRef]);

    useEffect(() => {
        if (!cellEditingSession || !editor) return;

        const onSelectionOnlyTransaction = ({ transaction }: EditorEvents['transaction']) => {
            if (!transaction.selectionSet || transaction.docChanged) return;

            activateEditorShortcutContext();

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorSelectionChange');
        };

        editor.on('transaction', onSelectionOnlyTransaction);

        return () => {
            editor.off('transaction', onSelectionOnlyTransaction);
        };
    }, [activateEditorShortcutContext, cellEditingSession, editor, hotTableInstanceRef]);

    useEffect(() => {
        // TODO-TABLES: This can be refactored to be less hacky after draft is removed
        const editorWrapper: MilanoteEditorRef<TiptapContent> = {
            getEditor: () => editor,
            getEditorState: () => (editor ? (editor.getJSON() as any) : null),
            onChange: (content) => editor?.commands.setContent(content as any),
            setPlainText: (text: string, inlineStyles?: string[]) =>
                editor?.commands.setContent(getTiptapContentFromPlainText(text, inlineStyles)),
        };
        cellEditorRef.current = editorWrapper;

        return () => {
            if (cellEditorRef.current === editorWrapper) cellEditorRef.current = null;
        };
    }, [cellEditorRef, editor]);

    useEffect(() => {
        // This lets handsontable know which element is the active editor
        tiptapEditorRef.current!.setAttribute('data-hot-input', 'true');
    }, []);

    return (
        <div className="handsontable-input TableTiptapCellEditor" style={{ textAlign: textAlignment || undefined }}>
            <EditorContent
                innerRef={tiptapEditorRef}
                className="TableTiptapCellRenderer-content"
                dir="auto"
                editor={editor}
            />
        </div>
    );
};

export default TiptapCellEditorComponent;
